import { clientDayKey, shortDateTime } from '../../shared/dateTime';
import { planPhase, runningInTms, type PlanPhase } from './planRows';
import { shiftDayKey, warsawDayStart, type TimelineWindow } from './timeline';
import { bracketsExtent } from './bracketRules';
import type { DriverActivity, DriverActivityKind, PlanItem, PlanSegmentKind } from './vehiclePlanApi';

/**
 * Harmonogram na karcie pojazdu — czysta logika osi (07.10.2026, prototyp
 * p. Mariusza `pojazd-BQO1011-v2.html`, folder `prototypy-mariusz/
 * karta-pojazdu-BQO1011`, odstępstwa w `UWAGI.md` obok).
 *
 * Prototyp to wzorzec wyglądu i logiki, nie kod: skala osi (1 / 7 / 14 /
 * 21 / 28 dni z zachowaniem środka kadru), podziałka godzinowa, wiersze
 * „W trakcie / Zaplanowane / Zakończone”, okna Z i R, odcinek spóźnienia
 * i tygodniowy czas jazdy. Tu są reguły; rysuje `VehicleGantt.tsx`.
 *
 * Dni są polskie (Europe/Warsaw), jak w całym harmonogramie (`timeline.ts`).
 */

/** Skala osi — pięć przycisków (prototyp, zmiana 7). */
export const SCALE_LEVELS = [1, 7, 14, 21, 28] as const;

/**
 * Skala z makiety FTL (15.10.2026, Etap 1, przełącznik `vehicleVariantsLane`):
 * makieta ma 1 / 3 / 7 / 14 dni (domyślnie 7), wzorzec nadrzędny z 13.10 —
 * 1 / 7 / 14 / 21 / 28 (domyślnie 14). Bierzemy sumę poziomów, żeby żaden nie
 * przepadł, a wartość domyślną z wzorca — ma pierwszeństwo (decyzja Bartka
 * 15.10.2026); 21 i 28 dni zostają też dlatego, że prototyp z 07.10 ich używa.
 */
export const MOCKUP_SCALE_LEVELS = [1, 3, 7, 14, 21, 28] as const;
export type ScaleDays = (typeof MOCKUP_SCALE_LEVELS)[number];

/**
 * Widok uproszczony — okna nałożone na siebie, na nich pasek realizacji;
 * pełny — osobno okno załadunku, realizacja, okno rozładunku (zmiana 6).
 */
export type GanttDetail = 'simple' | 'full';

export const DEFAULT_SCALE: ScaleDays = 14;
export const MOCKUP_DEFAULT_SCALE: ScaleDays = 14;
export const DEFAULT_DETAIL: GanttDetail = 'simple';

/** Poziomy skali i wartość domyślna — z makietą (Etap 1) albo jak w prototypie z 07.10. */
export interface ScaleOptions {
  levels: readonly ScaleDays[];
  defaultDays: ScaleDays;
}

export function scaleOptions(mockup: boolean): ScaleOptions {
  return mockup
    ? { levels: MOCKUP_SCALE_LEVELS, defaultDays: MOCKUP_DEFAULT_SCALE }
    : { levels: SCALE_LEVELS, defaultDays: DEFAULT_SCALE };
}

/** Klucz w `localStorage` — skala i widok zapamiętane u planisty (zmiana 17). */
export const GANTT_PREFS_KEY = 'marathon.carSchedule.view';

export interface GanttPrefs {
  days: ScaleDays;
  detail: GanttDetail;
}

export function isScale(n: unknown, levels: readonly number[] = SCALE_LEVELS): n is ScaleDays {
  return typeof n === 'number' && levels.includes(n);
}

/** `localStorage` albo `null`, gdy przeglądarka go nie daje (tryb prywatny, blokada). */
function defaultStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Zapamiętana skala i widok. Wszystko w try/catch: w trybie prywatnym albo
 * przy zablokowanych danych witryny dostęp rzuca, a zepsuty zapis nie może
 * psuć ekranu — wtedy wartości domyślne.
 */
export function readGanttPrefs(
  storage: Storage | null = defaultStorage(),
  scale: ScaleOptions = scaleOptions(false),
): GanttPrefs {
  const fallback = { days: scale.defaultDays, detail: DEFAULT_DETAIL };
  try {
    const raw = storage?.getItem(GANTT_PREFS_KEY);
    if (!raw) return fallback;
    const saved = JSON.parse(raw) as Partial<Record<keyof GanttPrefs, unknown>>;
    return {
      // Zapis spoza poziomów tej wersji (np. „3 dni” po wyłączeniu makiety) — domyślna.
      days: isScale(saved.days, scale.levels) ? saved.days : scale.defaultDays,
      detail: saved.detail === 'full' || saved.detail === 'simple' ? saved.detail : DEFAULT_DETAIL,
    };
  } catch {
    return fallback;
  }
}

export function writeGanttPrefs(prefs: GanttPrefs, storage: Storage | null = defaultStorage()): void {
  try {
    storage?.setItem(GANTT_PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // Brak zapisu to tylko brak wygody — ekran działa dalej.
  }
}

/** Pierwszy dzień widoku „dziś”: przy jednym dniu — dziś, inaczej dziś na środku kadru. */
export function homeStartKey(todayKey: string, days: ScaleDays): string {
  return days === 1 ? todayKey : shiftDayKey(todayKey, -Math.floor(days / 2));
}

/** Zmiana skali zostawia środek kadru na miejscu (jeden klik na poziom). */
export function rescaleStartKey(startKey: string, from: ScaleDays, to: ScaleDays): string {
  return shiftDayKey(startKey, Math.trunc((from - to) / 2));
}

/** Przewijanie: o dzień przy skali krótszej niż tydzień (1 i 3 dni), inaczej o tydzień. */
export function scrollStep(days: ScaleDays): number {
  return days < 7 ? 1 : 7;
}

/** Dni widoku jako `RRRR-MM-DD` — parametry zapytania API. */
export function viewKeys(startKey: string, days: number): { from: string; to: string } {
  return { from: startKey, to: shiftDayKey(startKey, days - 1) };
}

/** Okno osi: od początku pierwszego dnia polskiego do ostatniej milisekundy ostatniego. */
export function viewWindow(startKey: string, days: number): TimelineWindow {
  return {
    from: warsawDayStart(startKey),
    to: new Date(warsawDayStart(shiftDayKey(startKey, days)).getTime() - 1),
  };
}

const WEEKDAYS = ['nd', 'pn', 'wt', 'śr', 'cz', 'pt', 'sb'];

function keyParts(key: string): { y: number; m: number; d: number; weekday: number } {
  const [y, m, d] = key.split('-').map(Number);
  return { y, m, d, weekday: new Date(Date.UTC(y, m - 1, d)).getUTCDay() };
}

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * Zakres dat obok nawigacji (zmiana 8): „wt 06.10.2026” przy jednym dniu,
 * „30.09 – 13.10.2026” przy kilku; rok przy początku tylko na przełomie lat.
 */
export function rangeLabel(startKey: string, days: number): string {
  const a = keyParts(startKey);
  if (days === 1) return `${WEEKDAYS[a.weekday]} ${pad(a.d)}.${pad(a.m)}.${a.y}`;
  const b = keyParts(shiftDayKey(startKey, days - 1));
  const startYear = a.y !== b.y ? `.${a.y}` : '';
  return `${pad(a.d)}.${pad(a.m)}${startYear} – ${pad(b.d)}.${pad(b.m)}.${b.y}`;
}

/**
 * Ile części ma dzień na podziałce: godziny przy dniu, co 3 h przy trzech
 * dniach (makieta), co 6 h przy tygodniu, co 12 h do dwóch tygodni.
 */
export function hourParts(days: number): number {
  if (days === 1) return 24;
  if (days <= 3) return 8;
  if (days <= 7) return 4;
  if (days <= 14) return 2;
  return 1;
}

/** Podpisy podziałki dnia: „00”, „06”, „12”, „18”… */
export function hourLabels(parts: number): string[] {
  if (parts <= 1) return [];
  return Array.from({ length: parts }, (_, i) => pad((i * 24) / parts));
}

/**
 * Wiersz osi: trzy linie stanu z TMS (`PlanPhase`) i — z makietą FTL
 * (15.10.2026, Etap 1, `vehicleVariantsLane`) — osobny wiersz wariantów.
 */
export type GanttLane = PlanPhase | 'variants';

/** Wiersze osi (zmiana 12): nazwy z prototypu, kolejność — od tego, co teraz. */
export const GANTT_LANES: ReadonlyArray<{ lane: GanttLane; label: string }> = [
  { lane: 'inProgress', label: 'W trakcie' },
  { lane: 'planned', label: 'Zaplanowane' },
  { lane: 'done', label: 'Zakończone' },
];

/**
 * Wiersze osi z makietą FTL (wzorzec z 13.10: „W trakcie”, „Zaplanowane”,
 * „Warianty”, „Zakończone”): warianty — wstępne wskazania auta — nie mieszają
 * się z obsadzeniami.
 */
export const MOCKUP_GANTT_LANES: ReadonlyArray<{ lane: GanttLane; label: string }> = [
  { lane: 'inProgress', label: 'W trakcie' },
  { lane: 'planned', label: 'Zaplanowane' },
  { lane: 'variants', label: 'Warianty' },
  { lane: 'done', label: 'Zakończone' },
];

export function ganttLanes(variantsLane: boolean): ReadonlyArray<{ lane: GanttLane; label: string }> {
  return variantsLane ? MOCKUP_GANTT_LANES : GANTT_LANES;
}

/**
 * Wiersz pozycji. Bez wiersza wariantów — linia stanu z TMS (wariant
 * w zaplanowanych, jak dotąd). Z nim — każdy wariant w „Warianty”, a kolor
 * paska mówi stan z TMS.
 *
 * `runningNotVariant` (24.10.2026, S-24.10-06, decyzja użytkownika 21.10.2026,
 * C33): kandydatura do zlecenia, które TMS ma już w realizacji albo
 * zrealizowane, nie jest wariantem — stoi w linii stanu z TMS („W trakcie”,
 * „Zakończone”). Bez przełącznika — w „Warianty”, jak dotąd.
 */
export function laneOf(
  item: Pick<PlanItem, 'kind' | 'tmsOrderStatus'>,
  variantsLane: boolean,
  runningNotVariant = false,
): GanttLane {
  if (variantsLane && item.kind === 'candidate' && !(runningNotVariant && runningInTms(item))) return 'variants';
  return planPhase(item);
}

/** Przedział chwil (ISO) na osi. */
export interface TimeSpan {
  from: string;
  to: string;
}

/**
 * Okna zlecenia na osi (UWAGI.md: „Z/R z terminów punktów tylko gdy różne
 * od okna klienta, inaczej jedno okno klienta z podpisem”).
 *
 * - `client` — jedno okno zlecenia od klienta (początek okna załadunku —
 *   koniec okna rozładunku); tak jest prawie zawsze, bo TMS podaje to samo
 *   okno na każdym punkcie.
 * - `points` — terminy z planu TMS dla pierwszego załadunku (Z) i ostatniego
 *   rozładunku (R), gdy któryś różni się od okna klienta. Termin bez jednego
 *   z krańców dostaje go z okna klienta; brak terminu — brak okna.
 */
export type OrderWindows =
  | { mode: 'client'; client: TimeSpan | null }
  | { mode: 'points'; client: TimeSpan | null; loading: TimeSpan | null; unloading: TimeSpan | null };

const sameInstant = (a: string | null | undefined, b: string | null | undefined) =>
  !!a && !!b && new Date(a).getTime() === new Date(b).getTime();

export function orderWindows(
  item: Pick<PlanItem, 'windowFrom' | 'windowTo' | 'pointWindows'>,
): OrderWindows {
  const client =
    item.windowFrom && item.windowTo ? { from: item.windowFrom, to: item.windowTo } : null;
  const pw = item.pointWindows;
  const span = (w: { from: string | null; to: string | null } | null | undefined): TimeSpan | null => {
    if (!w) return null;
    const from = w.from ?? item.windowFrom;
    const to = w.to ?? item.windowTo;
    return from && to ? { from, to } : null;
  };
  const loading = span(pw?.loading);
  const unloading = span(pw?.unloading);
  const differs = (w: TimeSpan | null) =>
    w !== null && !(client && sameInstant(w.from, client.from) && sameInstant(w.to, client.to));
  if (!differs(loading) && !differs(unloading)) return { mode: 'client', client };
  return { mode: 'points', client, loading, unloading };
}

/**
 * Odcinek spóźnienia (prototyp: „odcinek spóźnienia poza oknem
 * rozładunku”): od końca okna rozładunku do szacowanego końca wykonania
 * (`overrunUntil` z API). Odcinki paska kończą się na końcu okna, jak
 * zajętość — ten odcinek tylko pokazuje, ile brakuje.
 */
export function lateSpan(item: Pick<PlanItem, 'windowTo' | 'overrunUntil'>): TimeSpan | null {
  if (!item.overrunUntil || !item.windowTo) return null;
  if (new Date(item.overrunUntil).getTime() <= new Date(item.windowTo).getTime()) return null;
  return { from: item.windowTo, to: item.overrunUntil };
}

const msOf = (v: string | null | undefined): number | null => {
  if (!v) return null;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? null : t;
};

/**
 * Miejsce pozycji na osi — do rozkładu na podtory: pasek, odcinki, okna
 * (jeśli rysowane), nawiasy Z/R (jeśli rysowane — 24.10.2026) i spóźnienie.
 * Bez żadnej daty — `null`.
 */
export function itemExtent(
  item: PlanItem,
  withWindows: boolean,
  withBrackets = false,
): { start: number; end: number } | null {
  const points: Array<string | null | undefined> = [item.occupancyFrom, item.occupancyTo];
  for (const s of item.segments ?? []) points.push(s.start, s.end);
  // Odcinek „wg planu TMS” (12.10.2026) stoi za paskiem — też zajmuje tor.
  if (item.tmsPlanSegment) points.push(item.tmsPlanSegment.end);
  const late = lateSpan(item);
  if (late) points.push(late.to);
  if (withWindows) {
    const w = orderWindows(item);
    points.push(w.client?.from, w.client?.to);
    if (w.mode === 'points') points.push(w.loading?.from, w.loading?.to, w.unloading?.from, w.unloading?.to);
  }
  const ms = points.map(msOf).filter((t): t is number => t !== null);
  if (withBrackets) ms.push(...bracketsExtent(item));
  if (ms.length === 0) return null;
  return { start: Math.min(...ms), end: Math.max(...ms) };
}

/** „31,5 h” — godziny z przecinkiem, jedna cyfra po nim. */
export function hoursText(h: number): string {
  const rounded = Math.round(h * 10) / 10;
  return `${String(rounded).replace('.', ',')} h`;
}

/** Rodzaj odcinka planu jako czynność kierowcy — do szacunku przyszłej aktywności. */
const SEGMENT_ACTIVITY: Record<PlanSegmentKind, DriverActivityKind> = {
  access: 'drive',
  drive: 'drive',
  loading: 'work',
  unloading: 'work',
  stop: 'work',
  break: 'break',
  rest: 'rest',
};

export interface EstimatedActivity {
  kind: DriverActivityKind;
  start: Date;
  end: Date;
  orderId: number;
  number: string | null;
}

/**
 * Plan jazdy kierowcy na przyszłość — z odcinków obsadzeń, które jeszcze
 * nie są zakończone (warianty to wstępne wskazanie, nie plan kierowcy).
 * Od `from` (chwila „teraz” albo koniec osi tachografu, późniejsza z nich)
 * — przeszłość mówi tachograf. Zawsze szacunek.
 */
export function estimatedActivity(items: readonly PlanItem[], from: Date): EstimatedActivity[] {
  const fromMs = from.getTime();
  const out: EstimatedActivity[] = [];
  for (const item of items) {
    if (item.kind !== 'assignment' || planPhase(item) === 'done') continue;
    for (const s of item.segments ?? []) {
      const start = Math.max(fromMs, new Date(s.start).getTime());
      const end = new Date(s.end).getTime();
      if (!(end > start)) continue;
      out.push({
        kind: SEGMENT_ACTIVITY[s.kind],
        start: new Date(start),
        end: new Date(end),
        orderId: item.orderId,
        number: item.number,
      });
    }
  }
  return out.sort((a, b) => a.start.getTime() - b.start.getTime());
}

const MS_PER_HOUR = 3_600_000;

export interface WeekDriving {
  weekStart: Date;
  weekEnd: Date;
  /** Jazda wg tachografu (h). */
  actualH: number;
  /** Jazda z planu po końcu osi (h) — szacunek. */
  estimateH: number;
  totalH: number;
  limitH: number;
  /** Udział w limicie, 1 = 100%. */
  share: number;
  percent: number;
  over: boolean;
  coveredFrom: string | null;
  coveredUntil: string;
}

/**
 * Czy oś tachografu ma co rysować: świeża (`ok`) albo starsza niż parametr
 * z zapisaną przeszłością (`pastOnly`, 24.10.2026, S-24.10-07).
 */
export function activityDrawable(activity: DriverActivity | null): boolean {
  return !!activity && (activity.status === 'ok' || activity.pastOnly === true);
}

/**
 * Od kiedy plan jazdy z obsadzeń (szacunek): przy świeżej osi — od jej końca
 * albo „teraz”, późniejsze; przy osi starszej niż parametr (`pastOnly`) — od
 * „teraz”: luki między odczytem a „teraz” nie wypełniamy niczym.
 */
export function estimateStart(activity: DriverActivity | null, now: Date): Date | null {
  if (!activityDrawable(activity)) return null;
  if (activity?.pastOnly) return now;
  return new Date(Math.max(now.getTime(), new Date(activity?.until ?? now).getTime()));
}

/**
 * „ostatni odczyt 22.10, 14:30 — starszy niż 24 h” (S-24.10-07); `null`,
 * gdy oś nie jest przeszłością bez „teraz”.
 */
export function staleCaption(activity: DriverActivity | null): string | null {
  if (!activity?.pastOnly || !activity.until) return null;
  // Czas polski jawnie (`shortDateTime`: „22.10, 14:30”), bez przecinka.
  const at = shortDateTime(activity.until).replace(',', '');
  return `ostatni odczyt ${at} — starszy niż ${activity.maxAgeHours ?? 24} h`;
}

/**
 * Tygodniowy czas jazdy (prototyp: pasek tygodnia z wypełnieniem i limitem;
 * zmiana 3: przekroczenie na czerwono z procentem). Fakty z tachografu do
 * końca osi, dalej jazda z planu obsadzeń (szacunek). Bez danych
 * z tachografu (`status` inny niż `ok`) — nic: nie wiemy, ile już jeździł.
 * Oś starsza niż parametr z przeszłością (`pastOnly`, S-24.10-07) — suma
 * z tego, co jest, a szacunek od „teraz”.
 */
export function weeklyDriving(
  activity: DriverActivity | null,
  items: readonly PlanItem[],
  now: Date,
): WeekDriving[] {
  if (!activity || !activityDrawable(activity)) return [];
  const estimateFrom = estimateStart(activity, now) ?? now;
  const estimate = estimatedActivity(items, estimateFrom).filter((a) => a.kind === 'drive');
  return activity.weeks.map((w) => {
    const ws = new Date(w.weekStart).getTime();
    const we = new Date(w.weekEnd).getTime();
    const estimateMs = estimate.reduce(
      (sum, a) => sum + Math.max(0, Math.min(we, a.end.getTime()) - Math.max(ws, a.start.getTime())),
      0,
    );
    const estimateH = estimateMs / MS_PER_HOUR;
    const totalH = w.drivenHours + estimateH;
    const share = activity.weeklyLimitHours > 0 ? totalH / activity.weeklyLimitHours : 0;
    return {
      weekStart: new Date(ws),
      weekEnd: new Date(we),
      actualH: w.drivenHours,
      estimateH,
      totalH,
      limitH: activity.weeklyLimitHours,
      share,
      percent: Math.round(share * 100),
      over: share > 1,
      coveredFrom: w.coveredFrom,
      coveredUntil: w.coveredUntil,
    };
  });
}

/**
 * Bieżące i następne zlecenie do paska podsumowania (zmiana 5). Bieżące —
 * w realizacji wg TMS, a bez takiego obsadzenie, którego szacowana
 * zajętość trwa teraz. Następne — najbliższe obsadzenie z zajętością
 * zaczynającą się później. Warianty to nie plan auta — nie liczą się.
 */
export function currentAndNext(
  items: readonly PlanItem[],
  now: Date,
): { current: PlanItem | null; next: PlanItem | null } {
  const t = now.getTime();
  const active = items.filter((i) => i.kind === 'assignment' && planPhase(i) !== 'done');
  const startOf = (i: PlanItem) => msOf(i.occupancyFrom) ?? msOf(i.windowFrom) ?? Infinity;
  const endOf = (i: PlanItem) => msOf(i.occupancyTo) ?? msOf(i.windowTo) ?? startOf(i);
  const current =
    active.find((i) => planPhase(i) === 'inProgress') ??
    active.find((i) => startOf(i) <= t && t < endOf(i)) ??
    null;
  const next =
    active
      .filter((i) => i !== current && startOf(i) > t)
      .sort((a, b) => startOf(a) - startOf(b))[0] ?? null;
  return { current, next };
}

/** Dzień polski „dziś” jako klucz — do przycisku „Dziś”. */
export function todayStartKey(now: Date, days: ScaleDays): string {
  return homeStartKey(clientDayKey(now), days);
}
